import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import SalesOrderForm from "../CreateSalesOrder/SalesOrderForm/SalesOrderForm";
import { emptySoForm, SO_API, soUrlKey, syncSoDocumentDate } from "../CreateSalesOrder/data";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { SoPage, SoPageHeader, SoStatusBadge, soColors } from "../CreateSalesOrder/soUi";

const toDateInput = (v) => (v ? String(v).slice(0, 10) : null);

const mapSoToForm = (so) =>
	syncSoDocumentDate({
		...emptySoForm(),
		...so,
		order_date: toDateInput(so.order_date),
		document_date: toDateInput(so.document_date || so.order_date),
		items: (so.items || []).map((row) => ({
			item_code: row.item_code,
			qty: row.qty,
			rate: row.rate != null && row.rate !== "" ? Number(row.rate) : null,
			id: row.id,
		})),
	});

const SelectedSalesOrder = () => {
	const [searchParams] = useSearchParams();
	const id = searchParams.get(soUrlKey);
	const [form, setForm] = useState(emptySoForm());
	const [meta, setMeta] = useState({ status: "DRAFT", so_number: "" });
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");

	const readOnly = meta.status && meta.status !== "DRAFT";

	const load = async () => {
		if (!id) return;
		setError("");
		try {
			const res = await axios.get(`${SO_API}/${id}`);
			const so = res.data.result;
			setMeta({ status: so.status, so_number: so.so_number, id: so.id });
			setForm(mapSoToForm(so));
		} catch (err) {
			setError(parseErrorMessage(err));
		}
	};

	useEffect(() => {
		load();
	}, [id]);

	const handleSave = async () => {
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const res = await axios.put(`${SO_API}/${id}`, form);
			setMessage("Draft saved");
			const so = res.data.result;
			setMeta({ status: so.status, so_number: so.so_number, id: so.id });
			setForm(mapSoToForm(so));
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
		}
	};

	const handlePublish = async () => {
		if (!window.confirm("Publish this SO? It will be locked (Ginesys sync worker comes in Phase 2).")) return;
		setSaving(true);
		setMessage("");
		setError("");
		try {
			await axios.put(`${SO_API}/${id}`, form);
			const res = await axios.post(`${SO_API}/${id}/publish`);
			setMessage(res.data.message || "Published");
			await load();
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
		}
	};

	if (!id) {
		return (
			<SoPage>
				<SoPageHeader
					title="Selected sales order"
					subtitle="Open a row from All sales orders to view or edit a draft."
				/>
			</SoPage>
		);
	}

	return (
		<SoPage sx={{ py: 1.25 }}>
			<SoPageHeader
				compact
				title={meta.so_number || `SO #${id}`}
				subtitle={meta.so_number ? `Id ${id}` : null}
				badge={<SoStatusBadge status={meta.status || "DRAFT"} />}
			/>

			{error && (
				<Typography sx={{ mb: 1.5, px: 0.5, fontSize: 13, fontWeight: 600, color: soColors.danger }}>
					{error}
				</Typography>
			)}
			{message && (
				<Typography sx={{ mb: 1.5, px: 0.5, fontSize: 13, fontWeight: 600, color: soColors.success }}>
					{message}
				</Typography>
			)}

			<SalesOrderForm
				variant="selected"
				value={form}
				onChange={setForm}
				readOnly={readOnly}
				onSave={handleSave}
				onPublish={readOnly ? undefined : handlePublish}
				saving={saving}
			/>
		</SoPage>
	);
};

export default SelectedSalesOrder;
